import { z } from 'zod';
const DateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const BillingInspectionTypeSchema = z.enum(['pv', 'bty', 'both']);
export const InspectionRateSchema = z.object({
    inspectionType: BillingInspectionTypeSchema,
    kwMin: z.number().nonnegative(),
    kwMax: z.number().positive().nullable(),
    price: z.number().nonnegative(),
});
export const ExtraRateSchema = z.object({
    code: z.string().min(1),
    label: z.string().min(1),
    price: z.number(),
    unit: z.enum(['each', 'job', 'night', 'km']).default('each'),
});
export const RateCardStatusSchema = z.enum(['draft', 'active', 'superseded']);
export const TravelClaimMethodSchema = z.enum(['per-km', 'fixed', 'quoted', 'not-claimable']);
export const TravelClaimScopeSchema = z.enum(['all', 'regional-only', 'conditional']);
export const AccommodationClaimMethodSchema = z.enum([
    'fixed-nightly',
    'actual-cost',
    'manual',
    'not-claimable',
]);
export const RateCardSchema = z.object({
    rateCardId: z.string().min(1),
    clientId: z.string().min(1),
    version: z.number().int().positive(),
    effectiveFrom: DateSchema,
    effectiveTo: DateSchema.nullable().default(null),
    status: RateCardStatusSchema,
    inspectionRates: z.array(InspectionRateSchema),
    travel: z.object({
        claimMethod: TravelClaimMethodSchema.default('per-km'),
        claimScope: TravelClaimScopeSchema.default('all'),
        routeBasis: z.enum(['supplier-day-round-trip', 'manual']).default('supplier-day-round-trip'),
        ratePerKm: z.number().nonnegative().default(0),
        freeKmThreshold: z.number().nonnegative().default(0),
        setRate: z.number().nonnegative().nullable().default(null),
        fuelLevyPerKm: z.number().nonnegative().default(0),
        evidenceRequired: z.boolean().default(false),
        notes: z.string().default(''),
        defaultBase: z
            .object({ label: z.string().min(1), address: z.string().min(1) })
            .default({ label: 'Geebung warehouse', address: 'Unit 9/94 Delta Street, Geebung QLD 4034' }),
        baseOverrides: z
            .array(z.object({
            inspectorId: z.string().min(1),
            label: z.string().min(1),
            address: z.string().min(1),
            notes: z.string().default(''),
        }))
            .default([]),
    }),
    accommodation: z.object({
        claimMethod: AccommodationClaimMethodSchema.default('fixed-nightly'),
        perNight: z.number().nonnegative().default(0),
        regionalVariants: z.array(z.object({ area: z.string().min(1), perNight: z.number().nonnegative() })),
        perPerson: z.boolean().default(false),
        evidenceRequired: z.boolean().default(false),
        notes: z.string().default(''),
    }),
    meals: z
        .object({
        claimable: z.boolean().default(false),
        breakfast: z.number().nonnegative().default(0),
        lunch: z.number().nonnegative().default(0),
        dinner: z.number().nonnegative().default(0),
        fullDay: z.number().nonnegative().default(0),
        evidenceRequired: z.boolean().default(false),
        notes: z.string().default(''),
    })
        // Zod 4: .default() short-circuits; .prefault() still runs nested field defaults.
        .prefault({}),
    extras: z.array(ExtraRateSchema),
    milestoneComponents: z.array(z.object({ code: z.string().min(1), label: z.string().min(1), price: z.number() })),
    gstTreatment: z.enum(['exGst', 'incGst']),
    sourceDocument: z
        .object({
        key: z.string(),
        fileName: z.string(),
        contentType: z.string(),
        uploadedAt: z.string(),
    })
        .nullable()
        .default(null),
    notes: z.string().default(''),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const UpsertRateCardSchema = RateCardSchema.omit({ createdAt: true, updatedAt: true });
const DEFAULT_TRAVEL_BASE = {
    defaultBase: {
        label: 'Geebung warehouse',
        address: 'Unit 9/94 Delta Street, Geebung QLD 4034',
    },
    baseOverrides: [],
};
/** Draft starting points transcribed from the T45 master invoice workbook. Confirm before activation. */
export const WORKBOOK_RATE_CARD_TEMPLATES = {
    techsafe: {
        travel: {
            ...DEFAULT_TRAVEL_BASE,
            claimMethod: 'per-km',
            claimScope: 'conditional',
            routeBasis: 'supplier-day-round-trip',
            ratePerKm: 1.25,
            freeKmThreshold: 100,
            setRate: null,
            fuelLevyPerKm: 0.75,
            evidenceRequired: false,
            notes: 'Local QLD rate. Other regions use set-rate or corridor rules that require confirmation.',
        },
        accommodation: {
            claimMethod: 'fixed-nightly',
            perNight: 200,
            regionalVariants: [],
            perPerson: false,
            evidenceRequired: false,
            notes: '',
        },
        meals: {
            claimable: false,
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            fullDay: 0,
            evidenceRequired: false,
            notes: '',
        },
        extras: [
            { code: 'confirmed-booking', label: 'Confirmed site booking', price: 20, unit: 'job' },
            {
                code: 'roof-access-markup',
                label: 'Roof access hire markup (enter charge manually)',
                price: 0,
                unit: 'each',
            },
        ],
        notes: 'Workbook draft: roof access is actual cost + 10%. Verify regional travel set rates before activation.',
    },
    itp: {
        travel: {
            ...DEFAULT_TRAVEL_BASE,
            claimMethod: 'per-km',
            claimScope: 'all',
            routeBasis: 'supplier-day-round-trip',
            ratePerKm: 2.1,
            freeKmThreshold: 100,
            setRate: null,
            fuelLevyPerKm: 0,
            evidenceRequired: false,
            notes: '',
        },
        accommodation: {
            claimMethod: 'actual-cost',
            perNight: 0,
            regionalVariants: [],
            perPerson: false,
            evidenceRequired: true,
            notes: 'Reimbursement based on actual cost incurred.',
        },
        meals: {
            claimable: true,
            breakfast: 22.5,
            lunch: 22.5,
            dinner: 32.5,
            fullDay: 77.5,
            evidenceRequired: false,
            notes: 'Breakfast 5am–11am; lunch 11am–4pm; dinner where travel continues after 5pm.',
        },
        extras: [{ code: 'island-site', label: 'Island job allowance', price: 100, unit: 'job' }],
        notes: 'Workbook-derived draft. Confirm current inspection fees and effective date before activation.',
    },
    gses: {
        travel: {
            ...DEFAULT_TRAVEL_BASE,
            claimMethod: 'per-km',
            claimScope: 'all',
            routeBasis: 'supplier-day-round-trip',
            ratePerKm: 2,
            freeKmThreshold: 100,
            setRate: null,
            fuelLevyPerKm: 0,
            evidenceRequired: false,
            notes: '',
        },
        accommodation: {
            claimMethod: 'fixed-nightly',
            perNight: 250,
            regionalVariants: [],
            perPerson: true,
            evidenceRequired: false,
            notes: '',
        },
        meals: {
            claimable: false,
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            fullDay: 0,
            evidenceRequired: false,
            notes: '',
        },
        extras: [],
        notes: 'Workbook-derived draft. Confirm current inspection fees and effective date before activation.',
    },
    kerfoot: {
        travel: {
            ...DEFAULT_TRAVEL_BASE,
            claimMethod: 'per-km',
            claimScope: 'all',
            routeBasis: 'supplier-day-round-trip',
            ratePerKm: 1.35,
            freeKmThreshold: 0,
            setRate: null,
            fuelLevyPerKm: 0,
            evidenceRequired: false,
            notes: 'Workbook heading does not state a free-kilometre threshold.',
        },
        accommodation: {
            claimMethod: 'fixed-nightly',
            perNight: 250,
            regionalVariants: [],
            perPerson: false,
            evidenceRequired: false,
            notes: '',
        },
        meals: {
            claimable: false,
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            fullDay: 0,
            evidenceRequired: false,
            notes: '',
        },
        extras: [
            {
                code: 'roof-access-markup',
                label: 'Roof access hire markup (enter charge manually)',
                price: 0,
                unit: 'each',
            },
        ],
        notes: 'Workbook draft: roof access is actual cost + 10%. Confirm the absence of a free-km threshold.',
    },
    mea: {
        travel: {
            ...DEFAULT_TRAVEL_BASE,
            claimMethod: 'per-km',
            claimScope: 'regional-only',
            routeBasis: 'supplier-day-round-trip',
            ratePerKm: 2.2,
            freeKmThreshold: 0,
            setRate: null,
            fuelLevyPerKm: 0,
            evidenceRequired: false,
            notes: 'Regional sites only.',
        },
        accommodation: {
            claimMethod: 'fixed-nightly',
            perNight: 265,
            regionalVariants: [],
            perPerson: false,
            evidenceRequired: false,
            notes: 'Regional sites only.',
        },
        meals: {
            claimable: false,
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            fullDay: 0,
            evidenceRequired: false,
            notes: '',
        },
        extras: [{ code: 'diesel-levy', label: 'Regional diesel levy', price: 0, unit: 'each' }],
        notes: 'Workbook-derived draft. Regional classification and diesel levy require confirmation.',
    },
    'smart-energy': {
        travel: {
            ...DEFAULT_TRAVEL_BASE,
            claimMethod: 'per-km',
            claimScope: 'all',
            routeBasis: 'supplier-day-round-trip',
            ratePerKm: 2.1,
            freeKmThreshold: 100,
            setRate: null,
            fuelLevyPerKm: 0,
            evidenceRequired: true,
            notes: 'Invoice line must reference the PVD and claimed kilometres; attach Google Maps route evidence.',
        },
        accommodation: {
            claimMethod: 'fixed-nightly',
            perNight: 270,
            regionalVariants: [],
            perPerson: true,
            evidenceRequired: false,
            notes: 'Workbook describes this as $270+ per person per night.',
        },
        meals: {
            claimable: false,
            breakfast: 0,
            lunch: 0,
            dinner: 0,
            fullDay: 0,
            evidenceRequired: false,
            notes: '',
        },
        extras: [
            {
                code: 'sa-inspection',
                label: 'South Australia inspection allowance',
                price: 65,
                unit: 'job',
            },
        ],
        notes: 'Workbook-derived draft. Confirm what the plus sign means before activation.',
    },
};
export const BillingExtraInputSchema = z.object({
    code: z.string().min(1),
    quantity: z.number().positive(),
});
/** Billing-owned Job facts that an administrator may correct while clearing run QC. */
export const BillingJobFactsPatchSchema = z.object({
    systemSizeKw: z.number().nonnegative().optional(),
    travelKm: z.number().nonnegative().optional(),
    accommodationNights: z.number().int().nonnegative().optional(),
    billingExtras: z.array(BillingExtraInputSchema).optional(),
    revisit: z.boolean().optional(),
    billingNotes: z.string().optional(),
    serviceM8JobNumber: z.string().min(1).optional(),
    resolutionNote: z.string().trim().min(1),
});
export const BillingLineSchema = z.object({
    jobId: z.string(),
    accreditationCodes: z.array(z.string()).min(1),
    completedAt: DateSchema,
    address: z.string(),
    ownerLastName: z.string(),
    serviceM8JobNumber: z.string().nullable(),
    inspectionType: BillingInspectionTypeSchema,
    systemSizeKw: z.number().nullable(),
    description: z.string(),
    rateCardId: z.string().nullable(),
    inspectionFee: z.number(),
    travel: z.number(),
    accommodation: z.number(),
    extras: z.number(),
    total: z.number(),
    pricingReason: z.array(z.string()),
    manuallyEdited: z.boolean().default(false),
    editNote: z.string().default(''),
});
export const TravelClaimStatusSchema = z.enum(['needs-review', 'ready', 'approved', 'excluded']);
export const TravelClaimSchema = z.object({
    travelClaimId: z.string().min(1),
    date: DateSchema,
    clientId: z.string().min(1),
    inspectorId: z.string().nullable(),
    jobIds: z.array(z.string().min(1)).min(1),
    baseLabel: z.string().min(1),
    baseAddress: z.string().min(1),
    stopAddresses: z.array(z.string().min(1)),
    routeKm: z.number().nonnegative().nullable(),
    routeSource: z.enum(['unresolved', 'job-facts', 'manual']),
    accommodationNights: z.number().int().nonnegative().default(0),
    actualAccommodationCost: z.number().nonnegative().nullable().default(null),
    mealQuantities: z
        .object({
        breakfast: z.number().nonnegative(),
        lunch: z.number().nonnegative(),
        dinner: z.number().nonnegative(),
        fullDay: z.number().nonnegative(),
    })
        .default({ breakfast: 0, lunch: 0, dinner: 0, fullDay: 0 }),
    travelAmount: z.number().nonnegative(),
    accommodationAmount: z.number().nonnegative(),
    mealAmount: z.number().nonnegative(),
    total: z.number().nonnegative(),
    rateCardId: z.string().nullable(),
    status: TravelClaimStatusSchema,
    calculationNotes: z.array(z.string()),
    manuallyEdited: z.boolean().default(false),
    reviewNote: z.string().default(''),
    reviewedAt: z.string().nullable().default(null),
});
export const TravelClaimPatchSchema = TravelClaimSchema.pick({
    baseLabel: true,
    baseAddress: true,
    routeKm: true,
    accommodationNights: true,
    actualAccommodationCost: true,
    mealQuantities: true,
    status: true,
}).extend({ reviewNote: z.string().trim().min(1) });
export const QcCheckSchema = z.object({
    code: z.string(),
    label: z.string(),
    severity: z.enum(['blocking', 'warning']),
    passed: z.boolean(),
    details: z.string(),
    inspectedCount: z.number().int().nonnegative().default(0),
    evidence: z
        .array(z.object({ jobId: z.string(), label: z.string(), detail: z.string() }))
        .default([]),
    acknowledgedAt: z.string().nullable().default(null),
    acknowledgementNote: z.string().default(''),
});
export const BillingRunStatusSchema = z.enum(['draft', 'approved', 'issued']);
export const BillingRunSchema = z.object({
    billingRunId: z.string().min(1),
    clientId: z.string().min(1),
    month: z.string().regex(/^\d{4}-\d{2}$/),
    status: BillingRunStatusSchema,
    lines: z.array(BillingLineSchema),
    travelClaims: z.array(TravelClaimSchema).default([]),
    qcChecks: z.array(QcCheckSchema),
    warningAcknowledgements: z.record(z.string(), z.string()).default({}),
    serviceM8Shell: z.object({
        enabled: z.literal(false),
        jobNumber: z.string().nullable(),
        payload: z.record(z.string(), z.unknown()),
        lines: z.array(z.object({
            name: z.string(),
            quantity: z.number(),
            price: z.number(),
            sortOrder: z.number(),
        })),
    }),
    invoiceNumber: z.string().nullable().default(null),
    invoiceDate: DateSchema.nullable().default(null),
    createdAt: z.string(),
    updatedAt: z.string(),
    approvedAt: z.string().nullable().default(null),
});
export const UninvoicedJobSchema = z.object({
    jobId: z.string(),
    clientId: z.string(),
    completedAt: DateSchema,
    ageDays: z.number().int(),
    address: z.string(),
    estimatedValue: z.number().nullable(),
    reason: z.string(),
});
//# sourceMappingURL=invoicing.js.map